import { useCallback, useEffect, useRef } from 'react';

import { useCancelBnplIntentMutation } from '@/hooks/mutations/booking/useCancelBnplIntentMutation';
import {
  clearBnplPaymentFromSession,
  getBnplPaymentFromSession,
} from '@/utils/bnplSessionStorage';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';

/**
 * What a recovery attempt found: `none` — no pending intent (or another
 * attempt already in flight); `recovered` — intent cancelled, cart unlocked,
 * `onRecovered` completed; `paid` — the intent already settled and `onPaid`
 * ran; `error` — the cancel failed (session kept so a later trigger retries).
 */
export type BnplRecoveryOutcome = 'none' | 'recovered' | 'paid' | 'error';

type UseBnplAbandonedRecoveryOptions = {
  cartId: string | undefined;
  /**
   * The pending intent was cancelled server-side and the cart is unlocked —
   * the screen should refresh the cart and release the Book button. Awaited,
   * so the caller can keep a single continuous loading state through the
   * whole recovery instead of flickering between its phases.
   */
  onRecovered: () => void | Promise<void>;
  /**
   * The customer actually completed payment at the provider (e.g. in another
   * tab) — the screen must route to the reservation view, never retry.
   */
  onPaid: (saleOrderId: string) => void;
};

/**
 * Recovers a cart abandoned mid-BNPL (SQR-15310 / SQR-15683). Once a BNPL
 * PaymentIntent is created the backend rejects every cart change until the
 * intent finalizes; a user who backs out of the provider flow (browser Back
 * on web, dismissing the payment sheet on native) fires no webhook, so the
 * cart would stay locked until the provider session times out. Cancelling
 * the intent via the API finalizes it synchronously, so retrying on the same
 * cart works immediately.
 *
 * Recovery runs automatically only on web (on mount, when the cart id
 * resolves, and on bfcache restores via `pageshow`): a live web session can
 * never coexist with an in-flight provider redirect, because the redirect
 * unloads the SPA. On native the session record exists WHILE the provider
 * flow is open on top of the app, so auto-running would cancel an active
 * payment — native triggers recovery explicitly from the BNPL sheet's
 * cancel handler instead.
 */
export const useBnplAbandonedRecovery = ({
  cartId,
  onRecovered,
  onPaid,
}: UseBnplAbandonedRecoveryOptions) => {
  const { mutateAsync: cancelBnplIntent } = useCancelBnplIntentMutation();

  // Held in a ref so the automatic web triggers below depend only on the cart
  // id. Callers pass callbacks that close over cart-updater functions, which
  // are rebuilt on every render — depending on them directly would recreate
  // the trigger each render and could re-issue a cancel after a failed one.
  const callbacksRef = useRef({ onRecovered, onPaid });
  useEffect(() => {
    callbacksRef.current = { onRecovered, onPaid };
  }, [onRecovered, onPaid]);

  // Multiple triggers can fire around the same moment (pageshow + cart-id
  // effect, or a cancel press landing on top of either). They must not issue
  // duplicate cancels, but a later caller still needs the real outcome — so
  // the in-flight promise is shared rather than the call being dropped.
  const inFlightRef = useRef<Promise<BnplRecoveryOutcome> | null>(null);

  const runRecovery = useCallback(
    async (
      pending: NonNullable<ReturnType<typeof getBnplPaymentFromSession>>,
      paymentIntentId: string,
      resolvedCartId: string,
    ): Promise<BnplRecoveryOutcome> => {
      try {
        const { status } = await cancelBnplIntent({
          paymentIntentId,
          cartId: resolvedCartId,
        });

        if (status === 'canceled') {
          // Clear only after the cart cleanup settles: if onRecovered throws,
          // the session record must survive so a later trigger (or the
          // Next-press guard) can still detach the stale payment.
          await callbacksRef.current.onRecovered();
          clearBnplPaymentFromSession();
          return 'recovered';
        }
        if (status === 'succeeded' || status === 'processing') {
          clearBnplPaymentFromSession();
          callbacksRef.current.onPaid(pending.cartId);
          return 'paid';
        }
        // Unexpected non-terminal status: keep the session so a later
        // trigger can retry, and leave the decision to the return flow.
        Logger.warn('BNPL abandoned recovery got unexpected intent status', {
          cartId: resolvedCartId,
          paymentIntentId,
          status,
        });
        return 'error';
      } catch (error) {
        // Keep the session record: the next trigger (focus, pageshow, cancel,
        // or the Next-press guard) retries the recovery.
        Logger.error('BNPL abandoned recovery failed', {
          error,
          cartId: resolvedCartId,
          paymentIntentId,
        });
        return 'error';
      }
    },
    [cancelBnplIntent],
  );

  const recoverAbandonedBnpl =
    useCallback(async (): Promise<BnplRecoveryOutcome> => {
      // A recovery already running owns this cart's pending intent; await its
      // real outcome so callers never release the Book button early.
      if (inFlightRef.current) return inFlightRef.current;
      if (!cartId) return 'none';
      const pending = getBnplPaymentFromSession(cartId);
      if (!pending?.paymentIntentId) return 'none';

      const attempt = runRecovery(pending, pending.paymentIntentId, cartId);
      inFlightRef.current = attempt;
      try {
        return await attempt;
      } finally {
        inFlightRef.current = null;
      }
    }, [cartId, runRecovery]);

  // Web: fresh page loads (browser Back without bfcache, or a reload) —
  // runs on mount and again once the cart id resolves from the cart query.
  useEffect(() => {
    if (!isWeb()) return;
    void recoverAbandonedBnpl();
  }, [recoverAbandonedBnpl]);

  // Web: bfcache restores re-enter the page without remounting React, so
  // effects don't re-run — but `pageshow` with `persisted` fires. The
  // function check guards non-browser environments that report as web but
  // shim `window` without DOM event methods (e.g. the native test preset).
  useEffect(() => {
    if (!isWeb() || typeof window.addEventListener !== 'function') return;
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) void recoverAbandonedBnpl();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [recoverAbandonedBnpl]);

  return { recoverAbandonedBnpl };
};
